import React, {useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import FolderSelector from 'file-browser/FolderSelector';
import HelpButton from 'help/HelpButton';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import * as RegistryUtil from 'registry/RegistryUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

// Keep in sync with benchling/notebook/structured_tables/constants.py:StructuredTableFolderOption
export enum StructuredTableFolderOption {
  DERIVE_FROM_ENTRY = 'DERIVE_FROM_ENTRY', // Deprecated
  DERIVE_FROM_PARENT_DOCUMENT_TYPE = 'DERIVE_FROM_PARENT_DOCUMENT_TYPE',
  NONE = 'NONE',
  SPECIFY_FOLDER = 'SPECIFY_FOLDER',
}

export interface StructuredTableFolderConfig {
  folderOption: StructuredTableFolderOption;
  /* Specific folder to use; required if folderOption is SPECIFY_FOLDER & otherwise undefined. */
  folderId?: string;
}

function _getDocumentTypeName(fileId?: string): string {
  if (fileId) {
    if (APIUtil.isWorksheet(fileId) || APIUtil.isProcedure(fileId)) {
      return 'Worksheet';
    }

    if (APIUtil.isRequestV2Submission(fileId) || APIUtil.isRequestV2Definition(fileId)) {
      return 'Request';
    }

    // TODO: add more type names in the future
    assertAndContinue(APIUtil.isEntry(fileId), 'Unrecognized fileId, defaulting to Entry');
  }

  return 'Entry';
}

export function getFolderOptionDisplayText(
  folderOption: StructuredTableFolderOption,
  itemTypeSupportsFolder: boolean,
  fileId?: string
): string {
  const documentTypeName = _getDocumentTypeName(fileId);
  switch (folderOption) {
    case StructuredTableFolderOption.NONE:
      return 'None';
    case StructuredTableFolderOption.DERIVE_FROM_ENTRY:
    case StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE:
      return itemTypeSupportsFolder
        ? `${documentTypeName}'s project folder`
        : `${documentTypeName}'s project`;
    case StructuredTableFolderOption.SPECIFY_FOLDER:
      return `Choose a ${itemTypeSupportsFolder ? 'folder' : 'project'}`;
    default:
      assert(false, `Unexpected folder option ${folderOption}`);
  }
}

export interface UseSelectFolderOptionForTableProps {
  projectIsRequired: boolean;
  itemTypeSupportsFolders: boolean;
  isForCreate: boolean;
  isReadOnly?: boolean;
  initialFolderConfig?: StructuredTableFolderConfig;
  useLegacyLabel?: boolean;
  structuredTableInsertionContext?: StructuredTableInsertionContextValue;
}

export interface UseSelectFolderOptionForTableResult {
  selectedFolderConfig: StructuredTableFolderConfig;
  folderOptionSelector: JSX.Element | null;
  isInvalidFolderConfig: boolean;
}

/**
 * Controls folder/project option state for creating items, and returns value and the dropdown option to toggle.
 *
 * @param itemTypeSupportsFolders - Whether current configuration supports folders (rather than just projects).
 * Controls option text.
 * @param projectIsRequired - If true, no dropdown rendered because there's only one option (derive
 * project/folder from entry).
 * @param isForCreate - If false, nothing is rendered, and a default value of
 * StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE and undefined folderId is returned. However,
 * these values can be overridden by setting initialFolderConfig.
 * @param isReadOnly - If true, render dropdown read only to display selected option.
 * @param initialFolderConfig - Override initial selected value.
 * @param useLegacyLabel - Render <Label> instead of <FieldLabel>. Unfortunately needed for consistency
 * between fields in TransferTableInsertModal -- should be migrated to use FieldLabel
 *
 * @returns
 *   - selectedFolderConfig: Config for how to derive folder/project for created items. Should be saved and passed to
 *     the structured table via the metadata.
 *   - isInvalidFolderConfig: True if the configuration is invalid, specifically SPECIFY_FOLDER has been selected
 *     but no folder was chosen. Should block saving the configuration.
 *   - folderOptionSelector: Possibly null checkbox element, to be inserted into the modal component to
 *     allow toggling selectedFolderOption.
 */
export default function useSelectFolderOptionForTable({
  itemTypeSupportsFolders,
  projectIsRequired,
  initialFolderConfig,
  isForCreate,
  isReadOnly,
  useLegacyLabel,
  structuredTableInsertionContext,
}: UseSelectFolderOptionForTableProps): UseSelectFolderOptionForTableResult {
  const defaultFolderOption =
    initialFolderConfig?.folderOption == null ||
    initialFolderConfig?.folderOption === StructuredTableFolderOption.DERIVE_FROM_ENTRY
      ? StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE
      : initialFolderConfig.folderOption;
  const defaultFolderId = initialFolderConfig?.folderId ?? undefined;

  const [selectedFolderOption, setSelectedFolderOption] =
    useState<StructuredTableFolderOption>(defaultFolderOption);
  const [selectedFolderId, setSelectedFolderId] = useState<Optional<string>>(defaultFolderId);

  // No need to display settings for non-create tables, since no folder/project is needed
  // We use the default values rather than stateful values so that if isForCreate is toggled, we don't
  // accidentally pass selected values that are no longer valid for the isForCreate case.
  if (!isForCreate) {
    return {
      selectedFolderConfig: {
        folderOption: defaultFolderOption,
        folderId: defaultFolderId,
      },
      isInvalidFolderConfig: false,
      folderOptionSelector: null,
    };
  }

  const handleSelectFolder = (e: {target: {value: string}}): void => {
    const {folderId} = RegistryUtil.parseFolderValue(e.target.value);
    setSelectedFolderId(folderId);
  };

  const dropdownLabel = itemTypeSupportsFolders ? 'Folder' : 'Project';
  const formLabel = useLegacyLabel ? (
    <Label>{dropdownLabel}</Label>
  ) : (
    <FieldLabel isRequired={true} label={dropdownLabel} />
  );
  const optionText = `Can be the same ${dropdownLabel.toLowerCase()} as the entry, a specific selected ${dropdownLabel.toLowerCase()}, or none to associate items only with the registry.`;
  const helpText = `${dropdownLabel} to associate with the created items. ${optionText}`;

  const allowedSettings = projectIsRequired
    ? [
        StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE,
        StructuredTableFolderOption.SPECIFY_FOLDER,
      ]
    : Object.keys(StructuredTableFolderOption).filter(
        (val) => val !== StructuredTableFolderOption.DERIVE_FROM_ENTRY
      );
  const insertedFileId = structuredTableInsertionContext?.itemInsertedFromId;
  const options = allowedSettings.map((opt: StructuredTableFolderOption) => ({
    key: opt,
    onSelect: setSelectedFolderOption,
    value: opt,
    label: getFolderOptionDisplayText(opt, itemTypeSupportsFolders, insertedFileId),
  }));
  const folderOptionSelector = (
    <FormGroup>
      {formLabel}
      <HelpButton
        iconClassName="u-small-spacing-left"
        renderContent={() => <p>{helpText}</p>}
        interaction={FloatingInteraction.HOVER}
      />
      <RadioButtonSelector
        options={options}
        onChange={(folderOption) => {
          setSelectedFolderOption(folderOption);
        }}
        selectedValue={selectedFolderOption}
        disabled={isReadOnly}
      />
      {selectedFolderOption === StructuredTableFolderOption.SPECIFY_FOLDER && (
        <div className="u-small-spacing-top">
          <FolderSelector
            value={selectedFolderId}
            onChange={handleSelectFolder}
            showInventory={itemTypeSupportsFolders}
            noFolderText={itemTypeSupportsFolders ? 'Choose folder' : 'Choose project'}
            readOnly={isReadOnly}
            enableRedesign={true}
          />
        </div>
      )}
    </FormGroup>
  );
  return {
    selectedFolderConfig: {
      folderOption: selectedFolderOption,
      folderId:
        selectedFolderOption === StructuredTableFolderOption.SPECIFY_FOLDER
          ? (selectedFolderId ?? undefined)
          : undefined,
    },
    isInvalidFolderConfig:
      selectedFolderOption === StructuredTableFolderOption.SPECIFY_FOLDER && selectedFolderId == null,
    folderOptionSelector,
  };
}
